import { FC, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { Grid, Skeleton, useTheme } from '@mui/material'

import { useGetTagListQuery, useGetTagGroupsListQuery } from '../../../../../api/core'
import { GRBanner } from '../../../../../components/GRBanner/GRBanner'
import { useCurrentUserLanguage } from '../../../../account/hooks/useCurrentUserLanguage'
import { TrackedGame } from '../../../../live-events/types/TrackedGame'
import {
  getDiscontinuedBlueprintIds,
  groupLiveEventExamples,
  LiveEventExamplesByGame,
  LiveEventGroupBy,
} from '../../../../live-events/utils/liveEventExamplesGrouping'
import { LiveEventBlueprintCard } from '../LiveEventBlueprintCard'
import { LiveEventTypeCard } from '../LiveEventTypeCard'

export { LiveEventGroupBy }

type LiveEventsListProps = {
  selectedGames: TrackedGame[]
  filteredEvents?: LiveEventExamplesByGame
  isLoading?: boolean
  groupBy?: LiveEventGroupBy
  hideDiscontinuedBlueprints?: boolean
}

export const LiveEventsList: FC<LiveEventsListProps> = ({
  selectedGames,
  filteredEvents,
  isLoading,
  groupBy = LiveEventGroupBy.Type,
  hideDiscontinuedBlueprints = false,
}) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const userLanguage = useCurrentUserLanguage()

  // Create a map of tracked games by game ID for quick lookup
  const gamesMap = useMemo(() => {
    return selectedGames.reduce((acc, trackedGame) => {
      acc[trackedGame.game.id] = trackedGame
      return acc
    }, {} as { [gameId: string]: TrackedGame })
  }, [selectedGames])

  // Fetch event type tags
  const { data: eventTags, isLoading: tagsLoading } = useGetTagListQuery({ type: 'event', userLanguage })

  // Fetch tag groups for event types
  const { data: tagGroups, isLoading: tagGroupsLoading } = useGetTagGroupsListQuery({ type: 'event', userLanguage })

  // Create a map of tag groups by event type ID
  const tagGroupsMap = useMemo(() => {
    if (!tagGroups) return {}

    const map: { [eventTypeId: string]: any } = {}
    tagGroups.forEach((group) => {
      if (group.tags) {
        group.tags.forEach((tagId) => {
          map[tagId] = group
        })
      }
    })
    return map
  }, [tagGroups])

  // Create a map of event tags by ID
  const eventTagsMap = useMemo(() => {
    if (!eventTags) return {}

    return eventTags.reduce((acc, tag) => {
      acc[tag.id] = tag
      return acc
    }, {} as { [tagId: string]: any })
  }, [eventTags])

  const discontinuedBlueprintIds = useMemo(() => getDiscontinuedBlueprintIds(selectedGames), [selectedGames])

  // Shared with the result summary (`useLiveEventResultCounts`), so the counts
  // above the list always describe exactly these cards.
  const { byType: eventsByType, byBlueprint: eventsByBlueprint } = useMemo(
    () =>
      groupLiveEventExamples({
        filteredEvents,
        groupBy,
        hideDiscontinuedBlueprints,
        discontinuedBlueprintIds,
        isKnownEventType: eventTags ? (typeId) => !!eventTagsMap[typeId] : undefined,
      }),
    [filteredEvents, groupBy, hideDiscontinuedBlueprints, discontinuedBlueprintIds, eventTags, eventTagsMap]
  )

  // Build a blueprint name lookup from all selected games
  const blueprintNamesMap = useMemo(() => {
    const map: { [blueprintId: string]: string } = {}

    selectedGames.forEach((trackedGame) => {
      trackedGame.blueprints?.forEach((blueprint) => {
        if (!map[blueprint.blueprintId]) {
          const name = blueprint.names[userLanguage as keyof typeof blueprint.names] || blueprint.names['en'] || blueprint.blueprintId
          if (name) map[blueprint.blueprintId] = name
        }
      })
    })

    return map
  }, [selectedGames, userLanguage])

  // Sort blueprint groups: alphabetically by blueprint name, "No Blueprint" groups last
  const sortedBlueprintGroups = useMemo(() => {
    return [...eventsByBlueprint].sort((a, b) => {
      // "No Blueprint" groups go last
      if (!a.blueprintId && b.blueprintId) return 1
      if (a.blueprintId && !b.blueprintId) return -1
      if (!a.blueprintId && !b.blueprintId) {
        // Both "No Blueprint" — sort by event type name
        const typeA = eventTagsMap[a.typeId]?.name || ''
        const typeB = eventTagsMap[b.typeId]?.name || ''
        return typeA.localeCompare(typeB, undefined, { sensitivity: 'base' })
      }

      // Both have blueprintId — sort by blueprint name
      const nameA = blueprintNamesMap[a.blueprintId!] || a.blueprintId!
      const nameB = blueprintNamesMap[b.blueprintId!] || b.blueprintId!
      return nameA.localeCompare(nameB, undefined, { sensitivity: 'base' })
    })
  }, [eventsByBlueprint, blueprintNamesMap, eventTagsMap])

  // Sort event types alphabetically by name
  const sortedEventTypes = useMemo(() => {
    return Object.entries(eventsByType)
      .sort(([typeIdA], [typeIdB]) => {
        const eventTypeA = eventTagsMap[typeIdA]
        const eventTypeB = eventTagsMap[typeIdB]

        if (!eventTypeA || !eventTypeB) {
          if (!eventTypeA && !eventTypeB) return 0
          if (!eventTypeA) return 1
          return -1
        }

        return eventTypeA.name.localeCompare(eventTypeB.name, undefined, { sensitivity: 'base' })
      })
      .map(([typeId]) => typeId)
  }, [eventsByType, eventTagsMap])

  const isLoadingData = tagsLoading || tagGroupsLoading || isLoading
  const hasNoGroups = groupBy === LiveEventGroupBy.Blueprint ? sortedBlueprintGroups.length === 0 : sortedEventTypes.length === 0

  if (isLoadingData) {
    return <LoadingSkeleton />
  }

  if (selectedGames.length === 0) {
    return <GRBanner severity="guidance">{t('implementations:no_games_selected_info')}</GRBanner>
  }

  if (hasNoGroups) {
    return <GRBanner severity="guidance">{t('implementations:no_live_events_found')}</GRBanner>
  }

  // Render blueprint grouping
  if (groupBy === LiveEventGroupBy.Blueprint) {
    return (
      <>
        {sortedBlueprintGroups.map((group) => {
          const eventType = eventTagsMap[group.typeId]
          const eventTypeName = eventType?.name || group.typeId
          const tagGroupColor = tagGroupsMap[group.typeId]?.colorHex || theme.palette.secondary.main

          return (
            <LiveEventBlueprintCard
              key={group.groupId}
              blueprintId={group.blueprintId}
              eventTypeName={eventTypeName}
              events={group.events}
              games={gamesMap}
              tagGroups={tagGroupsMap}
              tagGroupColor={tagGroupColor}
              isDiscontinued={!!group.blueprintId && discontinuedBlueprintIds.has(group.blueprintId)}
            />
          )
        })}
      </>
    )
  }

  // Render type grouping (default)
  return (
    <>
      {sortedEventTypes.map((typeId) => {
        const eventType = eventTagsMap[typeId]
        const events = eventsByType[typeId]

        if (!eventType || !events || events.length === 0) {
          return null
        }

        return <LiveEventTypeCard key={typeId} eventType={eventType} events={events} games={gamesMap} tagGroups={tagGroupsMap} />
      })}
    </>
  )
}

const LoadingSkeleton = () => {
  return (
    <Grid container spacing={2} wrap="wrap">
      {Array.from(Array(10).keys()).map((_, index) => (
        <Grid item xs={12} sm={6} md={4} lg={3} xl={3} key={index}>
          <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 5 }} />
        </Grid>
      ))}
    </Grid>
  )
}
